'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Clock3 } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { HeroGlobeBand } from '@/components/layout/HeroGlobeBand';
import { HeroBanner } from '@/components/ui/HeroBanner';
import { SectionsBlock } from '@/components/cms/SectionRenderer';
import { getPage, type HeroBannerSettings, type Page } from '@/lib/cms';
import { resourceHubApi, type ResourceArticle } from '@/lib/resource-hub-api';

type ArticleCard = {
  id: string;
  title: string;
  summary: string;
  category: string;
  readTime: string;
  href: string;
};

function stripHtml(html?: string | null): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function formatDate(value?: string | null): string {
  if (!value) return 'Latest';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Latest';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function estimateReadTime(content?: string | null): string {
  const text = stripHtml(content);
  const words = text ? text.split(' ').filter(Boolean).length : 0;
  const minutes = Math.max(1, Math.ceil(words / 220));
  return `${minutes} min read`;
}

function mapResourceArticle(article: ResourceArticle): ArticleCard {
  const summarySource = article.excerpt || stripHtml(article.content).slice(0, 180);
  const summary = summarySource ? `${summarySource}${summarySource.length >= 180 ? '...' : ''}` : 'Read more on EnthiosHub.';
  return {
    id: String(article.id),
    title: article.title,
    summary,
    category: article.category || 'Article',
    readTime: article.read_time || estimateReadTime(article.content),
    href: `/articles/${article.slug}`,
  };
}

export default function ArticlesPage() {
  const [page, setPage] = useState<Page | null>(null);
  const [articlesApi, setArticlesApi] = useState<ResourceArticle[]>([]);

  useEffect(() => {
    getPage('articles').then(setPage).catch(() => setPage(null));
    resourceHubApi.articles().then((res) => setArticlesApi(res.data.data ?? [])).catch(() => setArticlesApi([]));
  }, []);

  const heroSection = page?.sections?.find((section) => section.type === 'hero_banner');
  const title = page?.title || 'Articles';
  const description =
    stripHtml(page?.content) ||
    'Use these resources to plan, launch, and scale events confidently. Every article includes actionable steps your team can use right away.';

  const articles = useMemo(() => {
    if (articlesApi.length > 0) return articlesApi.slice(0, 9).map(mapResourceArticle);
    return [];
  }, [articlesApi]);

  return (
    <MainLayout
      heroHeader={
        heroSection ? (
          <HeroBanner data={(heroSection.settings ?? {}) as HeroBannerSettings} />
        ) : (
          <HeroGlobeBand
            badge="Documentation & Insights"
            title={title}
            subtitle={description}
            actions={
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  href="/blog"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2D3536] px-6 py-3 text-sm font-semibold text-text-inverse transition-all hover:bg-[#1e2425] shadow-sm"
                >
                  Browse all articles
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center rounded-xl border border-[#2D3536]/25 bg-bg/80 backdrop-blur-xs px-6 py-3 text-sm font-semibold text-[#2D3536] transition-all hover:bg-bg"
                >
                  Request a topic
                </Link>
              </div>
            }
          />
        )
      }
    >
      <div className="pb-20 space-y-10">

        {page?.sections && page.sections.length > 0 && <SectionsBlock sections={page.sections} />}

        <section className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-landing-text">Latest articles</h2>
              <p className="mt-1 text-sm text-landing-muted">Content from Resource Hub CMS, ordered by publish date.</p>
            </div>
            <Link href="/articles" className="inline-flex items-center gap-2 text-sm font-semibold text-landing-primary">
              View full articles
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {articles.length === 0 && (
              <article className="rounded-2xl border border-landing-border bg-bg p-6 lg:col-span-3">
                <h3 className="text-lg font-semibold text-landing-text">No articles published yet</h3>
                <p className="mt-2 text-sm leading-relaxed text-landing-muted">Check back soon for new content.</p>
              </article>
            )}
            {articles.map((article) => (
              <article key={article.id} className="rounded-2xl border border-landing-border bg-bg p-6">
                <div className="flex items-center gap-3 text-xs font-medium text-landing-muted">
                  <span className="rounded-full bg-landing-bg px-2.5 py-1">{article.category}</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock3 className="h-3.5 w-3.5" />
                    {article.readTime}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-landing-text">{article.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-landing-muted">{article.summary}</p>
                <Link href={article.href} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-landing-primary">
                  Read article
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-landing-border bg-bg p-6 sm:p-7">
          <h2 className="text-xl font-semibold text-landing-text">Need a specific guide?</h2>
          <p className="mt-2 text-sm text-landing-muted">
            Ask for a walkthrough and we will add practical content for your event type.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <Link
              href="/videos"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-landing-bg px-5 py-3 text-sm font-semibold text-landing-text"
            >
              Watch how-to videos
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-soot px-5 py-3 text-sm font-semibold text-text-inverse"
            >
              Contact content team
            </Link>
          </div>
          <p className="mt-3 text-xs text-landing-muted">Last updated: {formatDate(articlesApi[0]?.published_at)}</p>
        </section>
      </div>
    </MainLayout>
  );
}
